const SCROLL_SPEED = 225;
const POINTER_SPEED_MULTIPLIER = 1.5;
const FULL_SPEED_WIDTH = 1280;
const MIN_SPEED_SCALE = 0.45;
const MAX_FRAME_DELTA = 100;
const POINTER_RESPONSE = 90;
const AUTOPLAY_RESPONSE = 275;
const MANUAL_SCROLL_SETTLE = 150;
const SWIPE_MIN_DISTANCE = 48;
const SWIPE_MAX_DURATION = 600;
const SWIPE_DIRECTION_RATIO = 1.25;

const viewportCollections = new WeakSet();
const carouselCollections = new WeakSet();

const initializeViewportWidth = (collection) => {
  if (viewportCollections.has(collection)) return;
  viewportCollections.add(collection);

  const measure = () => {
    const margin =
      Number.parseFloat(getComputedStyle(collection).marginLeft) || 0;
    const origin = collection.getBoundingClientRect().left - margin;
    collection.style.setProperty(
      '--project-collection-viewport-width',
      `${document.documentElement.clientWidth}px`,
    );
    collection.style.setProperty(
      '--project-collection-viewport-offset',
      `${-origin}px`,
    );
  };

  measure();
  window.addEventListener('resize', measure);
  if ('ResizeObserver' in window) {
    const observer = new ResizeObserver(measure);
    // Watch every containing layout, since a nested column can move without
    // changing the viewport width. Do not observe our own expanded dimensions.
    for (
      let parent = collection.parentElement;
      parent;
      parent = parent.parentElement
    ) {
      observer.observe(parent);
    }
  }
};

const makeClone = (item) => {
  const clone = item.cloneNode(true);
  clone.dataset.projectCollectionClone = '';
  clone.setAttribute('aria-hidden', 'true');
  clone
    .querySelectorAll('[id]')
    .forEach((element) => element.removeAttribute('id'));
  clone
    .querySelectorAll('a, button, input, select, textarea, [tabindex]')
    .forEach((element) => element.setAttribute('tabindex', '-1'));
  return clone;
};

const initializeProjectCollection = (collection) => {
  if (carouselCollections.has(collection)) return;
  carouselCollections.add(collection);
  const rail = collection.querySelector('[data-project-collection-rail]');
  const sourceItems = rail
    ? Array.from(
        rail.querySelectorAll(
          ':scope > [data-project-collection-item]:not([data-project-collection-clone])',
        ),
      )
    : [];

  if (!rail || sourceItems.length === 0) return;

  let items = sourceItems;
  let isLooping = false;
  const count = sourceItems.length;
  const motionPreference = window.matchMedia(
    '(prefers-reduced-motion: reduce)',
  );
  const supportsHoverControl = window.matchMedia(
    '(hover: hover) and (pointer: fine)',
  ).matches;
  const autoplayVelocity = () =>
    SCROLL_SPEED *
    Math.min(
      Math.max(collection.clientWidth / FULL_SPEED_WIDTH, MIN_SPEED_SCALE),
      1,
    );
  let animationFrame;
  let lastTimestamp;
  let loopStart = 0;
  let loopLength = 0;
  let currentVelocity = autoplayVelocity();
  let targetVelocity = currentVelocity;
  let pointerPosition = 1;
  let isPointerOver = false;
  let hasFocus = false;
  let isInteracting = false;
  let isAwaitingScrollSettle = false;
  let isVisible = true;
  let scrollSettleTimer;
  let touchStart;

  const itemScrollLeft = (item) => {
    const railRect = rail.getBoundingClientRect();
    const itemRect = item.getBoundingClientRect();
    const inset = Number.parseFloat(getComputedStyle(rail).paddingLeft) || 0;
    return rail.scrollLeft + itemRect.left - railRect.left - inset;
  };

  const measureLoop = () => {
    loopStart = itemScrollLeft(items[count]);
    loopLength = itemScrollLeft(items[count * 2]) - loopStart;
  };

  const nearestItemIndex = () => {
    const inset = Number.parseFloat(getComputedStyle(rail).paddingLeft) || 0;
    const target = rail.getBoundingClientRect().left + inset;
    return items.reduce(
      (nearest, item, index) => {
        const distance = Math.abs(item.getBoundingClientRect().left - target);
        return distance < nearest.distance ? { distance, index } : nearest;
      },
      { distance: Number.POSITIVE_INFINITY, index: count },
    ).index;
  };

  const canRun = () =>
    isLooping &&
    !motionPreference.matches &&
    !hasFocus &&
    !isInteracting &&
    !isAwaitingScrollSettle &&
    isVisible &&
    !document.hidden;

  const desiredVelocity = () => {
    const autoplay = autoplayVelocity();
    return supportsHoverControl && isPointerOver
      ? autoplay * POINTER_SPEED_MULTIPLIER * pointerPosition
      : autoplay;
  };

  const normalizePosition = () => {
    if (!isLooping || loopLength <= 0) return;
    const offset = rail.scrollLeft - loopStart;
    // Browsers can round scrollLeft while card measurements retain fractions.
    // A subpixel difference at the start must not jump an entire loop.
    if (offset >= -1 && offset < loopLength) return;
    rail.scrollLeft =
      loopStart + (((offset % loopLength) + loopLength) % loopLength);
  };

  const stop = () => {
    window.cancelAnimationFrame(animationFrame);
    animationFrame = undefined;
    lastTimestamp = undefined;
  };

  const pauseImmediately = () => {
    stop();
    currentVelocity = 0;
    targetVelocity = 0;
  };

  const setTargetVelocity = (velocity) => {
    targetVelocity = velocity;
  };

  const tick = (timestamp) => {
    if (!canRun()) {
      stop();
      return;
    }

    if (lastTimestamp !== undefined) {
      const elapsed = Math.min(timestamp - lastTimestamp, MAX_FRAME_DELTA);
      const response = isPointerOver ? POINTER_RESPONSE : AUTOPLAY_RESPONSE;
      const blend = 1 - Math.exp(-elapsed / response);
      currentVelocity += (targetVelocity - currentVelocity) * blend;
      if (Math.abs(targetVelocity - currentVelocity) < 0.01) {
        currentVelocity = targetVelocity;
      }
      rail.scrollLeft += (currentVelocity * elapsed) / 1000;
      normalizePosition();
    }
    lastTimestamp = timestamp;
    animationFrame = window.requestAnimationFrame(tick);
  };

  const start = () => {
    if (animationFrame !== undefined || !canRun()) return;
    animationFrame = window.requestAnimationFrame(tick);
  };

  const resume = () => {
    setTargetVelocity(desiredVelocity());
    start();
  };

  const updatePointerVelocity = (clientX) => {
    const { left, width } = collection.getBoundingClientRect();
    const position = Math.min(Math.max((clientX - left) / width, 0), 1);
    pointerPosition = position * 2 - 1;
    setTargetVelocity(desiredVelocity());
  };

  const finishManualScroll = () => {
    if (isInteracting) return;
    window.clearTimeout(scrollSettleTimer);
    isAwaitingScrollSettle = false;
    normalizePosition();
    resume();
  };

  const scheduleManualScrollFinish = () => {
    window.clearTimeout(scrollSettleTimer);
    scrollSettleTimer = window.setTimeout(
      finishManualScroll,
      MANUAL_SCROLL_SETTLE,
    );
  };

  const advanceByCard = (direction) => {
    const currentIndex = nearestItemIndex();
    const targetIndex = Math.min(
      Math.max(currentIndex + direction, 0),
      items.length - 1,
    );
    rail.scrollTo({
      left: itemScrollLeft(items[targetIndex]),
      behavior: motionPreference.matches ? 'auto' : 'smooth',
    });
    scheduleManualScrollFinish();
  };

  rail.addEventListener('scroll', () => {
    if (!isInteracting && !isAwaitingScrollSettle) return;
    isAwaitingScrollSettle = true;
    scheduleManualScrollFinish();
  });
  rail.addEventListener('scrollend', () => {
    if (isAwaitingScrollSettle) finishManualScroll();
  });
  rail.addEventListener('pointerdown', (event) => {
    if (!isLooping) return;
    isInteracting = true;
    isAwaitingScrollSettle = true;
    window.clearTimeout(scrollSettleTimer);
    touchStart =
      event.pointerType === 'touch'
        ? {
            pointerId: event.pointerId,
            x: event.clientX,
            y: event.clientY,
            time: performance.now(),
          }
        : undefined;
    pauseImmediately();
  });
  const endPointerInteraction = (event) => {
    if (!isInteracting) return;
    isInteracting = false;
    if (touchStart?.pointerId === event.pointerId) {
      const distanceX = event.clientX - touchStart.x;
      const distanceY = event.clientY - touchStart.y;
      const duration = performance.now() - touchStart.time;
      const isSwipe =
        Math.abs(distanceX) >= SWIPE_MIN_DISTANCE &&
        Math.abs(distanceX) >= Math.abs(distanceY) * SWIPE_DIRECTION_RATIO &&
        duration <= SWIPE_MAX_DURATION;
      touchStart = undefined;
      if (isSwipe) {
        advanceByCard(distanceX < 0 ? 1 : -1);
        return;
      }
    }
    touchStart = undefined;
    scheduleManualScrollFinish();
  };
  window.addEventListener('pointerup', endPointerInteraction);
  window.addEventListener('pointercancel', () => {
    touchStart = undefined;
    if (!isInteracting) return;
    isInteracting = false;
    scheduleManualScrollFinish();
  });
  collection.addEventListener('pointerenter', (event) => {
    if (!supportsHoverControl || event.pointerType === 'touch') return;
    isPointerOver = true;
    updatePointerVelocity(event.clientX);
    start();
  });
  collection.addEventListener('pointermove', (event) => {
    if (!supportsHoverControl || event.pointerType === 'touch') return;
    updatePointerVelocity(event.clientX);
  });
  collection.addEventListener('pointerleave', (event) => {
    if (!supportsHoverControl || event.pointerType === 'touch') return;
    isPointerOver = false;
    setTargetVelocity(desiredVelocity());
    start();
  });
  collection.addEventListener('focusin', () => {
    hasFocus = true;
    pauseImmediately();
  });
  collection.addEventListener('focusout', (event) => {
    if (collection.contains(event.relatedTarget)) return;
    hasFocus = false;
    resume();
  });
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) stop();
    else resume();
  });
  const updateLayout = () => {
    const progress = loopLength
      ? (rail.scrollLeft - loopStart) / loopLength
      : 0;
    const style = getComputedStyle(rail);
    const gap = Number.parseFloat(style.columnGap) || 0;
    const availableWidth =
      rail.clientWidth -
      (Number.parseFloat(style.paddingLeft) || 0) -
      (Number.parseFloat(style.paddingRight) || 0);
    const contentWidth =
      sourceItems.reduce(
        (total, item) => total + item.getBoundingClientRect().width,
        0,
      ) +
      gap * (count - 1);
    const shouldLoop = count > 1 && contentWidth > availableWidth + 1;

    collection.classList.toggle('project-collection--fits', !shouldLoop);
    if (!shouldLoop) {
      pauseImmediately();
      window.clearTimeout(scrollSettleTimer);
      isInteracting = false;
      isAwaitingScrollSettle = false;
      touchStart = undefined;
      rail
        .querySelectorAll('[data-project-collection-clone]')
        .forEach((clone) => clone.remove());
      items = sourceItems;
      isLooping = false;
      loopStart = 0;
      loopLength = 0;
      rail.scrollLeft = 0;
      rail.removeAttribute('tabindex');
      return;
    }

    if (!isLooping) {
      const before = document.createDocumentFragment();
      const after = document.createDocumentFragment();
      sourceItems.forEach((item) => {
        before.append(makeClone(item));
        after.append(makeClone(item));
      });
      rail.prepend(before);
      rail.append(after);
      items = Array.from(
        rail.querySelectorAll(':scope > [data-project-collection-item]'),
      );
      isLooping = true;
      rail.setAttribute('tabindex', '0');
    }
    measureLoop();
    rail.scrollLeft = loopStart + progress * loopLength;
    normalizePosition();
    resume();
  };

  window.addEventListener('resize', updateLayout);
  if ('ResizeObserver' in window) {
    const observer = new ResizeObserver(updateLayout);
    observer.observe(rail);
    sourceItems.forEach((item) => observer.observe(item));
  }
  motionPreference.addEventListener('change', () => {
    if (motionPreference.matches) pauseImmediately();
    else resume();
  });

  if ('IntersectionObserver' in window) {
    const observer = new IntersectionObserver(([entry]) => {
      isVisible = entry.isIntersecting;
      if (isVisible) resume();
      else stop();
    });
    observer.observe(collection);
  }

  updateLayout();
};

export const initializeProjectCollections = () => {
  document
    .querySelectorAll('[data-project-collection-viewport]')
    .forEach(initializeViewportWidth);
  document
    .querySelectorAll('[data-project-collection]')
    .forEach(initializeProjectCollection);
};
